import * as React from 'react';
import { Check, ChevronsUpDown, Building2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  sidebarCollapseChevronClassName,
  sidebarCollapseLabelClassName,
  useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import API from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { HonoraryFlagValue } from '@paladin-monorepo/common';

import { HonoraryFlagBadge } from '@/components/account/HonoraryFlagBadge';
import { Logo } from './Logo';

export interface MembershipAccount {
  id: string;
  accountId: string;
  accountName?: string;
  accountType?: string;
  role: string;
}

interface AccountSwitcherProps {
  currentAccountId?: string;
  currentAccountName?: string;
  honoraryFlag?: HonoraryFlagValue | null;
  memberships: MembershipAccount[];
}

export function AccountSwitcher({
  currentAccountId,
  currentAccountName,
  honoraryFlag,
  memberships,
}: AccountSwitcherProps) {
  const { t } = useTranslation();
  const { isMobile } = useSidebar();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = React.useState(false);

  const handleAccountSwitch = async (accountId: string) => {
    if (accountId === currentAccountId || isLoading) return;

    setIsLoading(true);
    try {
      await API.post('/auth/switch-account', { accountId });
      await queryClient.invalidateQueries({ queryKey: ['/me'] });
      // Optionally reload the page to refresh all data
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch account:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Don't show switcher if user has only one account
  if (!memberships || memberships.length <= 1) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            size="lg"
            className="cursor-default"
            asChild
            tooltip={`Monomo - ${currentAccountName || t('sidebar.noAccount')}`}
          >
            <Link to="/">
              <Logo noName className="group-data-[collapsible=icon]:mx-auto" />
              {/* <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg"> */}
                {/* <Building2 className="size-4" /> */}
              {/* </div> */}
              <div
                className={cn(
                  'grid min-w-0 flex-1 text-left text-sm leading-tight',
                  sidebarCollapseLabelClassName,
                )}
              >
                <span className="truncate font-medium">Monomo</span>
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="truncate text-xs">
                    {currentAccountName || t('sidebar.noAccount')}
                  </span>
                </div>
              </div>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              disabled={isLoading}
              tooltip={
                isLoading
                  ? t('sidebar.switching')
                  : `Monomo - ${currentAccountName || t('sidebar.noAccount')}`
              }
            >
              <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <Building2 className="size-4" />
              </div>
              <div
                className={cn(
                  'grid min-w-0 flex-1 text-left text-sm leading-tight',
                  sidebarCollapseLabelClassName,
                )}
              >
                <span className="truncate font-medium">Monomo</span>
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="truncate text-xs">
                    {isLoading ? t('sidebar.switching') : currentAccountName || t('sidebar.noAccount')}
                  </span>
                  <HonoraryFlagBadge honoraryFlag={honoraryFlag} className="max-w-[min(100%,12rem)]" />
                </div>
              </div>
              <ChevronsUpDown className={sidebarCollapseChevronClassName} />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="start"
            sideOffset={4}
          >
            <DropdownMenuLabel>{t('sidebar.switchAccount')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {memberships.map((membership) => (
              <DropdownMenuItem
                key={membership.id}
                onClick={() => handleAccountSwitch(membership.accountId)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <div className="flex-1">
                  <div className="font-medium">{membership.accountName || membership.accountId}</div>
                  <div className="text-xs text-muted-foreground">
                    {membership.accountType} - {membership.role}
                  </div>
                </div>
                {membership.accountId === currentAccountId && (
                  <Check className="size-4 text-primary" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
